/**
 * Mirrors the local participant's UI state (hand-raised, reactions) onto every
 * other participant in the room.
 *
 * - Hand-raise → LiveKit participant metadata. Setting the local
 *   metadata triggers `ParticipantMetadataChanged` on every remote, so each
 *   client sees the flag flip without us routing anything ourselves.
 * - Reactions → LiveKit data channel (topic "reaction"). Each reaction is a
 *   small JSON packet `{emoji, ts}`; receivers emit it through the same local
 *   `emitReaction` queue so all clients render an identical flying-emoji
 *   animation simultaneously. The sender's identity/name isn't put on the
 *   wire — LiveKit already attaches the sending `Participant` as `msg.from`,
 *   so we read attribution straight off that instead of duplicating it.
 *
 * This hook must be rendered inside a `<LiveKitRoom>` provider so the LiveKit
 * hooks resolve.
 */

import { useEffect, useMemo, useRef } from "react";
import {
  useDataChannel,
  useLocalParticipant,
  useRoomContext,
} from "@livekit/components-react";
import { ConnectionState, RoomEvent } from "livekit-client";
import { withHandRaised } from "../lib/handRaiseMetadata";

/** Attribution for a reaction — who sent it. */
export interface ReactionSender {
  identity: string;
  name?: string;
}

interface UseCallBroadcastArgs {
  /** Whether the local participant has their hand raised. */
  handRaised: boolean;
  /**
   * Called when a remote reaction packet arrives. The caller pipes this into
   * the same `emitReaction` queue that the local reaction button uses, so the
   * floating-emoji animation is identical for everyone.
   */
  onRemoteReaction: (emoji: string, sender: ReactionSender) => void;
}

const REACTION_TOPIC = "reaction";

interface UseCallBroadcastResult {
  /** Publish a reaction packet to every other participant. */
  publishReaction: (emoji: string) => void;
}

export function useCallBroadcast({
  handRaised,
  onRemoteReaction,
}: UseCallBroadcastArgs): UseCallBroadcastResult {
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();
  const decoder = useMemo(() => new TextDecoder(), []);

  // Track the last value we pushed so reconnects can re-publish without
  // depending on the React render that flipped the toggle.
  const handRaisedRef = useRef<boolean>(handRaised);
  handRaisedRef.current = handRaised;

  // Push the local handRaised state onto our participant metadata. LiveKit
  // takes care of propagating the new metadata to every remote.
  useEffect(() => {
    if (!localParticipant) return;
    if (room?.state !== ConnectionState.Connected) return;
    const next = withHandRaised(localParticipant.metadata, handRaised);
    if (next === localParticipant.metadata) return;
    void localParticipant.setMetadata(next);
  }, [handRaised, localParticipant, room?.state]);

  // Re-publish metadata after a reconnect — the server discards local
  // metadata on a fresh join, so a long network blip would otherwise drop the
  // raised hand silently.
  useEffect(() => {
    if (!room) return;
    const onReconnected = () => {
      if (!localParticipant) return;
      if (!handRaisedRef.current) return;
      const next = withHandRaised(localParticipant.metadata, true);
      void localParticipant.setMetadata(next);
    };
    room.on(RoomEvent.Reconnected, onReconnected);
    return () => {
      room.off(RoomEvent.Reconnected, onReconnected);
    };
  }, [room, localParticipant]);

  // Reactions — dedicated data-channel topic so listeners don't have to
  // discriminate against chat / other future broadcasts via a payload field.
  const { send } = useDataChannel(REACTION_TOPIC, (msg) => {
    if (msg.from?.identity === localParticipant?.identity) {
      // We already render our own reaction locally for instant feedback; don't
      // re-process the echo when LiveKit fans it back out.
      return;
    }
    if (!msg.from?.identity) {
      // Can't attribute this reaction to anyone — drop it rather than show
      // an unowned emoji.
      return;
    }
    try {
      const parsed = JSON.parse(decoder.decode(msg.payload)) as {
        emoji?: unknown;
      };
      if (typeof parsed.emoji === "string" && parsed.emoji.length > 0) {
        onRemoteReaction(parsed.emoji, {
          identity: msg.from.identity,
          name: msg.from.name,
        });
      }
    } catch {
      // ignore — non-JSON or wrong shape
    }
  });

  const publishReaction = (emoji: string) => {
    if (room?.state !== ConnectionState.Connected) return;
    const encoded = new TextEncoder().encode(JSON.stringify({ emoji, ts: Date.now() }));
    void send(encoded, { reliable: true });
  };

  return { publishReaction };
}
